import { requireEnv } from '@/lib/env'
/**
 * Chat Session Hook
 *
 * Manages chat session state and persistence.
 * Supports two modes:
 * - Subscription mode: Uses anonymous auth + GraphQL subscriptions for real-time updates
 * - Polling mode: Falls back to REST API polling if subscriptions fail
 */

import { useState, useCallback, useEffect, useRef } from 'react'
import {
  signInAnonymously,
  getAnonymousSession,
  getPublicApolloClient,
  hasValidAnonymousSession,
} from '@/lib/apollo-client-public'
import { GuestChatMessagesDocument } from '@/graphql/chatbot-operations'

export interface ChatMessage {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  senderType?: 'ai' | 'staff' | 'guest'
  staffName?: string
  createdAt: string
}

export interface ChatSession {
  id: string
  sessionCode: string
  tenantId: string
  propertyId: string | null
  language: string
  aiEnabled: boolean
  expiresAt: string
}

export interface UseChatSessionReturn {
  session: ChatSession | null
  messages: ChatMessage[]
  isLoading: boolean
  isStaffHandling: boolean
  error: string | null
  initSession: (tenantId: string, propertyId?: string | null) => Promise<void>
  addUserMessage: (content: string) => void
  addAssistantMessage: (content: string) => void
  clearError: () => void
}

// Local storage key prefix
const STORAGE_KEY_PREFIX = 'chatbot_session_'
const GUEST_USER_KEY = 'chatbot_guest_user'

// Polling intervals (fallback when subscriptions fail)
const POLL_INTERVAL_STAFF = 3000 // 3 seconds when staff is handling
const POLL_INTERVAL_AI = 10000 // 10 seconds when AI is handling

interface ApiMessage {
  id: string
  role: string
  content: string
  senderType?: string
  staffName?: string
  createdAt: string
}

interface ApiSession {
  id: string
  sessionCode: string
  tenantId: string
  propertyId: string | null
  language: string
  aiEnabled?: boolean
  expiresAt: string
}

export function useChatSession(tenantId?: string): UseChatSessionReturn {
  const [session, setSession] = useState<ChatSession | null>(null)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [useSubscriptions, setUseSubscriptions] = useState(true)

  // Refs to prevent multiple simultaneous calls
  const isInitializingRef = useRef(false)
  const isRestoringRef = useRef(false)
  const hasAttemptedRestoreRef = useRef(false)
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const lastMessageIdRef = useRef<string | null>(null)
  const sessionCodeRef = useRef<string | null>(null)
  const lastPollTimeRef = useRef<number>(0)
  const subscriptionRef = useRef<{ unsubscribe: () => void } | null>(null)
  const guestUserIdRef = useRef<string | null>(null)
  const subscriptionErrorCountRef = useRef(0)
  const MAX_SUBSCRIPTION_ERRORS = 3 // Only fall back after 3 consecutive errors

  // Check if staff is handling (AI disabled)
  const isStaffHandling = session ? !session.aiEnabled : false

  // Check if session has expired
  const isSessionExpired = useCallback((expiresAt: string): boolean => {
    return new Date(expiresAt) < new Date()
  }, [])

  // Check session expiry periodically
  useEffect(() => {
    if (!session) return

    const checkExpiry = () => {
      if (isSessionExpired(session.expiresAt)) {
        setSession(null)
        setMessages([])
        setError('Your session has expired. Please start a new conversation.')
        if (tenantId) {
          localStorage.removeItem(`${STORAGE_KEY_PREFIX}${tenantId}`)
        }
      }
    }

    // Check every minute
    const interval = setInterval(checkExpiry, 60 * 1000)

    return () => clearInterval(interval)
  }, [session, tenantId, isSessionExpired])

  // Keep refs in sync for stable polling
  useEffect(() => {
    sessionCodeRef.current = session?.sessionCode ?? null
  }, [session?.sessionCode])

  // Parse API response to ChatMessage
  const parseMessage = (msg: ApiMessage): ChatMessage => ({
    id: msg.id,
    role: msg.role as ChatMessage['role'],
    content: msg.content,
    senderType: msg.senderType as ChatMessage['senderType'],
    staffName: msg.staffName,
    createdAt: msg.createdAt,
  })

  // Parse API response to ChatSession
  const parseSession = (data: ApiSession): ChatSession => ({
    id: data.id,
    sessionCode: data.sessionCode,
    tenantId: data.tenantId,
    propertyId: data.propertyId,
    language: data.language,
    aiEnabled: data.aiEnabled ?? true,
    expiresAt: data.expiresAt,
  })

  // Sign in anonymously and get user ID
  const ensureAnonymousAuth = useCallback(async (): Promise<string | null> => {
    // Check if we already have a valid anonymous session
    if (hasValidAnonymousSession()) {
      const session = getAnonymousSession()
      return session?.user?.id ?? null
    }

    // Check localStorage for existing guest user
    const stored = localStorage.getItem(GUEST_USER_KEY)
    if (stored) {
      try {
        const { userId, expiresAt } = JSON.parse(stored)
        if (new Date(expiresAt) > new Date()) {
          // Try to refresh the existing session
          const session = getAnonymousSession()
          if (session?.user?.id === userId) {
            return userId
          }
        }
      } catch {
        localStorage.removeItem(GUEST_USER_KEY)
      }
    }

    // Sign in anonymously
    const result = await signInAnonymously({
      sessionType: 'chatbot',
    })

    if (!result.success || !result.userId) {
      console.warn('[Chatbot] Anonymous auth failed, falling back to polling')
      setUseSubscriptions(false)
      return null
    }

    // Store guest user info
    localStorage.setItem(
      GUEST_USER_KEY,
      JSON.stringify({
        userId: result.userId,
        expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days
      })
    )

    guestUserIdRef.current = result.userId

    // Wait a tick for the SDK to fully store the session
    // This prevents race condition where WebSocket connects before session is stored
    await new Promise(resolve => setTimeout(resolve, 100))

    return result.userId
  }, [])

  // Set up subscription for real-time messages
  const setupSubscription = useCallback((sessionId: string) => {
    if (!useSubscriptions) return

    // Verify we have a valid anonymous session before setting up subscription
    const anonSession = getAnonymousSession()
    if (!anonSession?.accessToken) {
      console.warn('[Chatbot] No valid anonymous session for subscription, falling back to polling')
      setUseSubscriptions(false)
      return
    }

    // Clean up existing subscription
    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe()
      subscriptionRef.current = null
    }

    try {
      const client = getPublicApolloClient()

      // Plain gql document (no TypedDocumentNode); type the result explicitly
      const observable = client.subscribe<{ chat_messages?: Array<{ id: string; role: string; content: string; sender_type?: string; staff_name?: string; created_at: string }> }>({
        query: GuestChatMessagesDocument,
        variables: { sessionId },
      })

      subscriptionRef.current = observable.subscribe({
        next: ({ data }) => {
          // Reset error count on successful data
          subscriptionErrorCountRef.current = 0

          if (data?.chat_messages) {
            const newMessages = data.chat_messages.map((msg: {
              id: string
              role: string
              content: string
              sender_type?: string
              staff_name?: string
              created_at: string
            }) => ({
              id: msg.id,
              role: msg.role as ChatMessage['role'],
              content: msg.content,
              senderType: msg.sender_type as ChatMessage['senderType'],
              staffName: msg.staff_name,
              createdAt: msg.created_at,
            }))

            // Only update if there are new messages
            if (newMessages.length > 0) {
              // length > 0 checked above
              const lastMsg = newMessages[newMessages.length - 1]!
              if (lastMsg.id !== lastMessageIdRef.current) {
                lastMessageIdRef.current = lastMsg.id
                setMessages(newMessages)
              }
            }
          }
        },
        error: (err: unknown) => {
          console.error('[Chatbot] Subscription error:', err)
          subscriptionErrorCountRef.current++

          // Only fall back to polling after multiple consecutive errors
          if (subscriptionErrorCountRef.current >= MAX_SUBSCRIPTION_ERRORS) {
            console.warn('[Chatbot] Too many subscription errors, falling back to polling')
            setUseSubscriptions(false)
          }
        },
      })

    } catch (err) {
      console.error('[Chatbot] Failed to set up subscription:', err)
      setUseSubscriptions(false)
    }
  }, [useSubscriptions])

  // Poll for new messages - fallback when subscriptions fail
  const pollMessages = useCallback(async () => {
    const sessionCode = sessionCodeRef.current
    if (!sessionCode) return

    // Debounce: prevent polling more than once per second
    const now = Date.now()
    if (now - lastPollTimeRef.current < 1000) {
      return
    }
    lastPollTimeRef.current = now

    try {
      const response = await fetch(`/api/chatbot/session?code=${sessionCode}`)
      if (!response.ok) return

      const data = await response.json()
      if (!data.success) return

      // Update session (including aiEnabled status)
      setSession(parseSession(data.session))

      // Check for new messages
      const apiMessages: ApiMessage[] = data.messages || []
      if (apiMessages.length > 0) {
        // length > 0 checked above
        const lastApiMessage = apiMessages[apiMessages.length - 1]!

        // Only update if there are new messages
        if (lastApiMessage.id !== lastMessageIdRef.current) {
          lastMessageIdRef.current = lastApiMessage.id
          setMessages(apiMessages.map(parseMessage))
        }
      }
    } catch (err) {
      console.error('[Chatbot] Poll error:', err)
    }
  }, [])

  // Set up polling when subscriptions are disabled
  useEffect(() => {
    if (!session || useSubscriptions) {
      // No polling needed
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
        pollIntervalRef.current = null
      }
      return
    }

    // Clear existing interval
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current)
    }

    // Set interval based on who's handling
    const interval = isStaffHandling ? POLL_INTERVAL_STAFF : POLL_INTERVAL_AI
    pollIntervalRef.current = setInterval(pollMessages, interval)

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
        pollIntervalRef.current = null
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- session?.sessionCode is the primitive dep we care about, not the full session object
  }, [isStaffHandling, session?.sessionCode, pollMessages, useSubscriptions])

  // Cleanup subscription on unmount
  useEffect(() => {
    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe()
        subscriptionRef.current = null
      }
    }
  }, [])

  // Try to restore session from localStorage on mount
  useEffect(() => {
    if (!tenantId) return
    if (hasAttemptedRestoreRef.current) return
    if (session) return

    hasAttemptedRestoreRef.current = true

    const storageKey = `${STORAGE_KEY_PREFIX}${tenantId}`
    const stored = localStorage.getItem(storageKey)

    if (stored) {
      try {
        const { sessionCode, expiresAt } = JSON.parse(stored)

        if (new Date(expiresAt) > new Date()) {
          restoreSession(sessionCode)
        } else {
          localStorage.removeItem(storageKey)
        }
      } catch {
        localStorage.removeItem(storageKey)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- restoreSession is intentionally excluded to avoid re-triggering restore; tenantId and session are the only meaningful triggers
  }, [tenantId, session])

  const restoreSession = async (sessionCode: string) => {
    if (isRestoringRef.current || isInitializingRef.current) {
      return
    }

    isRestoringRef.current = true
    setIsLoading(true)

    try {
      // Ensure anonymous auth for subscriptions
      await ensureAnonymousAuth()

      const response = await fetch(`/api/chatbot/session?code=${sessionCode}`)

      if (!response.ok) {
        if (tenantId) {
          localStorage.removeItem(`${STORAGE_KEY_PREFIX}${tenantId}`)
        }
        return
      }

      const data = await response.json()

      if (data.success) {
        const restoredSession = parseSession(data.session)
        setSession(restoredSession)
        const apiMessages: ApiMessage[] = data.messages || []
        setMessages(apiMessages.map(parseMessage))

        if (apiMessages.length > 0) {
          lastMessageIdRef.current = apiMessages[apiMessages.length - 1]!.id
        }

        // Set up subscription for real-time updates
        if (useSubscriptions) {
          setupSubscription(restoredSession.id)
        }
      }
    } catch (err) {
      console.error('[Chatbot] Failed to restore session:', err)
    } finally {
      setIsLoading(false)
      isRestoringRef.current = false
    }
  }

  const initSession = useCallback(
    async (newTenantId: string, propertyId?: string | null) => {
      if (isInitializingRef.current || isRestoringRef.current) {
        return
      }

      if (session && session.tenantId === newTenantId) {
        return
      }

      isInitializingRef.current = true
      setIsLoading(true)
      setError(null)

      try {
        // Get anonymous user ID for subscriptions
        const guestUserId = await ensureAnonymousAuth()

        // Create session via ai-engine backend
        const aiEngineUrl = requireEnv(process.env.NEXT_PUBLIC_AI_ENGINE_URL, 'NEXT_PUBLIC_AI_ENGINE_URL')
        const response = await fetch(`${aiEngineUrl}/widget/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tenantId: newTenantId,
            propertyId: propertyId || undefined,
            language: 'ar',
          }),
        })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          throw new Error(errorData.error || errorData.message || 'Failed to create session')
        }

        const data = await response.json()

        if (!data.sessionCode) {
          throw new Error(data.error || 'Failed to create session')
        }

        // Map ai-engine response to the session format expected by the widget
        const newSession: ChatSession = {
          id: data.id,
          sessionCode: data.sessionCode,
          tenantId: newTenantId,
          propertyId: propertyId || null,
          language: 'ar',
          aiEnabled: true,
          expiresAt: data.expiresAt || new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        }
        setSession(newSession)
        setMessages([])
        lastMessageIdRef.current = null

        // Store session code in localStorage
        const storageKey = `${STORAGE_KEY_PREFIX}${newTenantId}`
        localStorage.setItem(
          storageKey,
          JSON.stringify({
            sessionCode: newSession.sessionCode,
            expiresAt: newSession.expiresAt,
          })
        )

        // Set up subscription for real-time updates
        if (useSubscriptions && guestUserId) {
          setupSubscription(newSession.id)
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to initialize chat'
        setError(errorMessage)
        console.error('[Chatbot] Init session error:', errorMessage)
      } finally {
        setIsLoading(false)
        isInitializingRef.current = false
      }
    },
    [session, ensureAnonymousAuth, setupSubscription, useSubscriptions]
  )

  const addUserMessage = useCallback((content: string) => {
    const newMessage: ChatMessage = {
      id: `user_${Date.now()}`,
      role: 'user',
      content,
      senderType: 'guest',
      createdAt: new Date().toISOString(),
    }
    setMessages((prev) => [...prev, newMessage])
  }, [])

  const addAssistantMessage = useCallback((content: string, senderType: 'ai' | 'staff' = 'ai', staffName?: string) => {
    const newMessage: ChatMessage = {
      id: `assistant_${Date.now()}`,
      role: 'assistant',
      content,
      senderType,
      staffName,
      createdAt: new Date().toISOString(),
    }
    setMessages((prev) => [...prev, newMessage])
  }, [])

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  return {
    session,
    messages,
    isLoading,
    isStaffHandling,
    error,
    initSession,
    addUserMessage,
    addAssistantMessage,
    clearError,
  }
}
